import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { mindApi } from '@/services/api';
import ThoughtCard from '@/components/mind/ThoughtCard';
import { Loader2 } from 'lucide-react';

const MODE_FILTERS = ['ALL', 'REFLECT', 'CONSOLIDATE', 'CURIOSITY', 'DISCOVER'];

export default function DiaryTab({ appId, realtimeThoughts = [] }) {
  const [thoughts, setThoughts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [modeFilter, setModeFilter] = useState('ALL');
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);

  // Combine real-time thoughts with paginated thoughts, deduplicate by cycle_id
  const allThoughts = React.useMemo(() => {
    const combined = [...realtimeThoughts, ...thoughts];
    const seen = new Set();
    return combined.filter((t) => {
      if (seen.has(t.cycle_id)) return false;
      seen.add(t.cycle_id);
      // Apply mode filter to real-time thoughts too
      if (modeFilter !== 'ALL' && t.mode !== modeFilter) return false;
      return true;
    });
  }, [realtimeThoughts, thoughts, modeFilter]);

  const fetchThoughts = useCallback(async (cursor = null, mode = modeFilter) => {
    if (!appId) return;

    const params = { limit: 20 };
    if (mode !== 'ALL') params.mode = mode;
    if (cursor) params.before = cursor;

    try {
      const res = await mindApi.getThoughts(appId, params);
      const data = res.data?.data || res.data;
      return {
        thoughts: data.thoughts || [],
        next_cursor: data.next_cursor || null,
      };
    } catch (err) {
      throw err;
    }
  }, [appId, modeFilter]);

  // Initial load + filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchThoughts(null, modeFilter)
      .then((result) => {
        if (cancelled) return;
        setThoughts(result.thoughts);
        setNextCursor(result.next_cursor);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [appId, modeFilter, fetchThoughts]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await fetchThoughts(nextCursor, modeFilter);
      setThoughts((prev) => [...prev, ...result.thoughts]);
      setNextCursor(result.next_cursor);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode filter buttons */}
      <div className="flex gap-2 flex-wrap">
        {MODE_FILTERS.map((mode) => (
          <Button
            key={mode}
            variant={modeFilter === mode ? 'default' : 'outline'}
            size="sm"
            className="text-xs h-7"
            onClick={() => setModeFilter(mode)}
          >
            {mode}
          </Button>
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="text-center text-sm text-destructive py-8">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && allThoughts.length === 0 && (
        <div className="text-center text-muted-foreground py-16">
          <p className="text-sm">No thoughts recorded yet.</p>
          <p className="text-xs mt-1">Thoughts appear after the heartbeat processes events.</p>
        </div>
      )}

      {/* Thought timeline */}
      {!loading && allThoughts.length > 0 && (
        <div className="space-y-3">
          {allThoughts.map((thought) => (
            <ThoughtCard key={thought.cycle_id} thought={thought} />
          ))}
        </div>
      )}

      {/* Load more */}
      {nextCursor && !loading && (
        <div className="flex justify-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
